import './ModalRoot.pcss';
import React, { useCallback, useLayoutEffect, useState } from 'react';
import { Dispatch } from 'redux';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import classnames from 'classnames';
import modalActions from '@mm/state/modules/modalsState/actions';
import { ESCAPE_KEY_NAME } from '@mm/constants/keys-codes';
import { MODAL_LOOKUP } from './constants';
import { StoreState } from '@mm/state/store-interfaces';
import { useSafeTimeout } from '@mm/hooks';
import { BASIC_MODAL_TEMPLATE_ID } from './BasicModalTemplate';
import { useKeyDown } from '../hooks/use-key-down';

export const MODAL_OPEN_BODY_CLASS = 'body--modal-open';

type OuterProps = {};

type InnerProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

type Props = InnerProps & OuterProps;

const mapStateToProps = (state: StoreState) => state.modalState;

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  closeModal: modalType => dispatch(modalActions.closeModal(modalType)),
});

const useOverflowBody = (isOpen: boolean) => {
  const { safeTimeout } = useSafeTimeout();
  const [originalScrollPosition, setOriginalScrollPosition] = useState(0);

  useLayoutEffect(() => {
    if (isOpen) {
      setOriginalScrollPosition(document.documentElement.scrollTop);
      document.body.classList.add(MODAL_OPEN_BODY_CLASS);
    } else {
      safeTimeout(() => {
        document.body.classList.remove(MODAL_OPEN_BODY_CLASS);
        document.documentElement.scrollTop = originalScrollPosition;
        setOriginalScrollPosition(0);
      }, 0);
    }
  }, [isOpen]);
};

export const ModalRootView = ({
  modalType,
  modalProps,
  isOpen,
  closeModal,
}: Props) => {
  const [isOldLayout, setIsOldLayout] = useState(false);
  const ActiveModal = MODAL_LOOKUP[modalType];
  const modalRoot = document.getElementById('modal-root');
  const onCloseModal = () => closeModal(modalType);
  useOverflowBody(isOpen);

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (modalProps && modalProps.paywall) {
        return;
      }

      if (modalType && e.key === ESCAPE_KEY_NAME) {
        closeModal(modalType);
      }
    },
    [modalType, closeModal],
  );

  useKeyDown(onKeyDown);

  // TODO: Find better way to do this that user does not have to pass special param
  useLayoutEffect(() => {
    const element = document.getElementById(BASIC_MODAL_TEMPLATE_ID);
    setIsOldLayout(!element);
  }, [modalType]);

  const classList = classnames(
    'modal-container',
    `modal-container__${modalType}`,
    {
      'modal-container--is-opening': isOpen,
      'modal-container--is-closing': !isOpen,
      'modal-container--old-design': isOldLayout,
    },
  );
  const modalClassList = classnames(
    'modal-container__modal',
    `modal-container__modal__${modalType}`,
  );

  if (modalType && modalRoot && ActiveModal) {
    return ReactDOM.createPortal(
      <div className={classList}>
        <div className="modal-container__overlay" />
        <div className="modal-container__wrapper">
          <div className={modalClassList}>
            <ActiveModal {...modalProps} closeModal={onCloseModal} />
          </div>
        </div>
      </div>,
      modalRoot,
    );
  }

  return null;
};

const withGarnish = compose<InnerProps, OuterProps>(
  connect(mapStateToProps, mapDispatchToProps),
);

export const ModalRoot = withGarnish(ModalRootView);
